import React, { useState } from "react";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setCheckoutStep, setTermsAccepted,setWithdrawalWaiverAccepted } from "@store/reducers/cart";

import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import { ERoutes } from "@/utils/constants";
import { computeSubscriptionPricing } from "@/utils/pricing";

import {
  BackBtn,
  ButtonGroup,
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  IconCircle,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  SlidePanelFooter,
  StepHeaderWrap,
  StepSubtitle,
  StepTitle,
} from "./styles";

const LegalLink = styled.a`
  color: #ebc28e;
  text-decoration: none;
  &:visited, &:link, &:active, &:focus { color: #ebc28e; }
  &:hover { color: #ebc28e; text-decoration: underline; }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="rgb(97, 84, 73)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* PHONE-ONLY fixed header (modal-template-phone.md): centred title, no icon,
   symmetric ✕ spacing (16px above the ✕ + 36px ✕ + 16px below = min-height 68;
   side-only padding). Hidden ≥601px so desktop/tablet keep the centred in-body
   icon-circle header. `data-slide-header` lets CartModal's measure() count it. */
const PhoneHeader = styled.div.attrs({ "data-slide-header": "" })`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-height: 68px;
    padding: 0 24px;

    h2 {
      font-family: "Cormorant Garamond", serif;
      font-size: 1.5rem;
      font-weight: 400;
      color: #fff;
      margin: 0;
      line-height: 1.2;
      text-align: center;
    }

    & + [data-slide-body] {
      padding-top: 0;
    }
  }
`;

/* The original centred icon-circle header — desktop/tablet only. */
const DesktopHeader = styled(StepHeaderWrap)`
  @media (max-width: 600px) {
    display: none;
  }
`;

/* First section label hugs the fixed header on phone (shared 18px top margin is
   for between-section gaps, not the very first one). */
const FirstSectionLabel = styled(SectionLabel)`
  @media (max-width: 600px) {
    margin-top: 2px;
  }
`;

/* Button label swaps to a shorter "Go to Payment" on phone. */
const LabelDesktop = styled.span`
  @media (max-width: 600px) { display: none; }
`;
const LabelPhone = styled.span`
  display: none;
  @media (max-width: 600px) { display: inline; }
`;

/* Footer actions: Back + primary side-by-side at every width. On phone, Back
   shrinks to its (tiny) content width and the primary flexes to fill the rest,
   so "Go to Payment" gets enough room to stay on one line instead of a wasteful
   50/50 split that wrapped it. */
const Actions = styled(ButtonGroup)`
  margin-top: 0;
  border-top: none;
  padding-top: 0;

  @media (max-width: 600px) {
    ${BackBtn} {
      flex: 0 0 auto;
      width: auto;
      padding-left: 20px;
      padding-right: 20px;
    }
    ${PrimaryBtn} {
      flex: 1;
      width: auto;
    }
  }
`;

const SubReviewView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { subscription, termsAccepted } = useAppSelector((state) => state.cart);
  const subConfig = useSubscriptionConfig();

  const { channels, claims, billingPeriod } = subscription;
  const isAnnual = billingPeriod === "annual";
  const pricing = computeSubscriptionPricing(subConfig, { channels, claims, isAnnual });
  const premiumBase = pricing.baseMonthly;
  const addonsPrice = pricing.addonsMonthly; // claims billed per channel
  // Round to cents at every aggregation. The discounted monthly base can
  // be $26.10 (29 × 0.9), and `26.10 × 12` in JS evaluates to
  // 313.20000000000005 due to floating-point — round eagerly so the
  // displayed totals stay clean.
  const monthlyTotal = Math.round((premiumBase + addonsPrice) * 100) / 100;
  const annualTotal = Math.round(monthlyTotal * 12 * 100) / 100;

  // In the store, not local state: SubPaymentView is what posts this to the
  // API, and it used to send a hardcoded `true` regardless of what was ticked.
  const waiverAccepted = useAppSelector((state) => state.cart.withdrawalWaiverAccepted);
  const setWaiverAccepted = (v: boolean) => dispatch(setWithdrawalWaiverAccepted(v));
  const canContinue = termsAccepted && waiverAccepted;

  // Clicking the (greyed) Continue button while a box is unchecked flashes a
  // red border + shakes the unchecked box(es) — same pattern as the payment step.
  const [attempted, setAttempted] = useState(false);
  const [shakeTerms, setShakeTerms] = useState(false);
  const [shakeWaiver, setShakeWaiver] = useState(false);
  const handleContinue = () => {
    if (canContinue) {
      dispatch(setCheckoutStep("sub-payment"));
      return;
    }
    setAttempted(true);
    if (!termsAccepted) { setShakeTerms(true); setTimeout(() => setShakeTerms(false), 500); }
    if (!waiverAccepted) { setShakeWaiver(true); setTimeout(() => setShakeWaiver(false), 500); }
  };

  return (
    <>
      {/* PHONE: fixed header — centred title, no icon (✕ stays top-right). */}
      <PhoneHeader>
        <h2>Review &amp; Accept</h2>
      </PhoneHeader>

      <SlidePanelBody>
      {/* DESKTOP/TABLET: original centred icon-circle header (hidden on phone). */}
      <DesktopHeader>
        <IconCircle><ShieldIcon /></IconCircle>
        <div>
          <StepTitle>Review & Accept</StepTitle>
          <StepSubtitle>Review your subscription and accept the terms</StepSubtitle>
        </div>
      </DesktopHeader>

      {/* Order summary */}
      <FirstSectionLabel><span>Your Subscription</span></FirstSectionLabel>
      <div style={{
        background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: "12px", padding: "16px", marginBottom: "24px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
        fontSize: "0.875rem",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginBottom: "6px", color: "#fff", fontWeight: 600, fontSize: "1.15rem" }}>
          <span>{isAnnual ? "Annual" : "Monthly"} Subscription</span>
          <span>{isAnnual ? `$${annualTotal.toFixed(2)}/year` : `$${monthlyTotal.toFixed(2)}/mo`}</span>
        </div>
        <div style={{ fontSize: "0.8125rem", color: "rgba(251, 244, 236, 0.5)", lineHeight: 1.5, textAlign: "center" }}>
          {channels} YouTube {channels === 1 ? "channel" : "channels"} · {claims} monthly claims{channels > 1 ? " per channel" : ""}
          {isAnnual && <> · 12 months billed upfront · {subConfig.annualDiscountPercent}% saved</>}
        </div>
      </div>

      {/* Terms */}
      <SectionLabel><span>Terms &amp; Conditions</span></SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
        <ShakeWrap $shake={shakeTerms}>
        <CheckboxRow onClick={() => dispatch(setTermsAccepted(!termsAccepted))} style={{ cursor: "pointer", border: `1px solid ${attempted && !termsAccepted ? "#e47a6a" : "transparent"}` }}>
          <CheckboxBox $checked={termsAccepted}>
            {termsAccepted && <CheckIcon />}
          </CheckboxBox>
          <CheckboxLabel>
            I agree to the{" "}
            <LegalLink href={ERoutes.Conditions} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Terms of Use</LegalLink>,{" "}
            <LegalLink href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Privacy Policy</LegalLink>,{" "}
            <LegalLink href={ERoutes.LicenceTerms} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>YouTube Licence Terms</LegalLink>, and{" "}
            <LegalLink href={ERoutes.RefundPolicy} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Refund Policy</LegalLink>.
          </CheckboxLabel>
        </CheckboxRow>
        </ShakeWrap>

        {/* EU 14-day withdrawal waiver — required for digital subscriptions */}
        <ShakeWrap $shake={shakeWaiver}>
        <CheckboxRow onClick={() => setWaiverAccepted(!waiverAccepted)} style={{ cursor: "pointer", border: `1px solid ${attempted && !waiverAccepted ? "#e47a6a" : "transparent"}` }}>
          <CheckboxBox $checked={waiverAccepted}>
            {waiverAccepted && <CheckIcon />}
          </CheckboxBox>
          <CheckboxLabel>
            I want immediate access to the subscription and acknowledge that I waive my 14-day right of withdrawal under EU consumer law.
          </CheckboxLabel>
        </CheckboxRow>
        </ShakeWrap>
      </div>
      </SlidePanelBody>

      <SlidePanelFooter>
        <Actions>
          <BackBtn onClick={() => dispatch(setCheckoutStep("plan-config"))}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg> Back</BackBtn>
          <PrimaryBtn
            onClick={handleContinue}
            style={!canContinue ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
          >
            <ButtonLabel glyph="arrowRight" muted={!canContinue}>
              <LabelDesktop>Continue to Payment</LabelDesktop>
              <LabelPhone>Go to Payment</LabelPhone>
            </ButtonLabel>
          </PrimaryBtn>
        </Actions>
      </SlidePanelFooter>
    </>
  );
};

export default SubReviewView;
